import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";
import { loadFromSupabase, saveToSupabase } from "../supabaseDB";
import { LOGO_B64 } from "./SalesReports";
import { loadReportBlob, openBlobInTab, saveDocBlob, loadDocBlob } from "./organicAudit/auditStorage";
import AuditComments from "./organicAudit/AuditComments";

/* ═══════════════════════════════════════════════════════════════════════════
   AUDIT ADVISOR PORTAL SHELL

   Separate, read-mostly portal for the external organic-audit advisor
   (user_profiles.role === 'audit_advisor'). Access is invitation-only,
   provisioned from UserManagement like shippers.

   What the advisor can do:
     - See ALL audits (any status) — read only.
     - View / download the reports Arturo generated (cannot generate them).
     - Upload their own supporting documents.   (Part 3)
     - Leave comments.                            (Part 4)

   This file is Part 1: the shell (header + Ground-Fresh logo + logout +
   ES/EN toggle, Spanish by default) and the audit LIST. The per-audit
   detail view (reports, uploads, comments) is wired in later parts.
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  es: {
    portal: "Portal de Asesor de Auditoría",
    welcome: "Bienvenido",
    logout: "Cerrar sesión",
    auditsTitle: "Auditorías",
    auditsSub: "Selecciona una auditoría para ver sus reportes y documentos.",
    colName: "Auditoría", colYear: "Año", colStatus: "Estado", colAuditor: "Auditor",
    colReports: "Reportes", colDocs: "Documentos", colPeriod: "Período",
    statusOpen: "Abierta", statusClosed: "Cerrada",
    empty: "No hay auditorías para mostrar.",
    loading: "Cargando…",
    open: "Abrir",
    back: "← Volver a auditorías",
    reportsTitle: "Reportes Generados",
    reportsEmpty: "No hay reportes generados en esta auditoría.",
    docsTitle: "Documentos",
    docsEmpty: "No hay documentos cargados.",
    repCol: "Reporte", dateCol: "Fecha / Hora",
    view: "Ver", opening: "Abriendo…", noFile: "Sin archivo",
    uploadBtn: "Subir documento", uploading: "Subiendo…",
    docNameCol: "Documento", uploadedByCol: "Cargado por", sizeCol: "Tamaño",
    byAdvisor: "Asesor", byAdmin: "GFI",
    uploadErr: "No se pudo subir el documento. Intenta de nuevo.",
    docRemarks: "Observaciones", hideRemarks: "Ocultar",
    statusCol: "Estado", stOk: "Correcto", stIssue: "Requiere observación", stNone: "Sin revisar",
  },
  en: {
    portal: "Audit Advisor Portal",
    welcome: "Welcome",
    logout: "Sign out",
    auditsTitle: "Audits",
    auditsSub: "Select an audit to view its reports and documents.",
    colName: "Audit", colYear: "Year", colStatus: "Status", colAuditor: "Auditor",
    colReports: "Reports", colDocs: "Documents", colPeriod: "Period",
    statusOpen: "Open", statusClosed: "Closed",
    empty: "No audits to show.",
    loading: "Loading…",
    open: "Open",
    back: "← Back to audits",
    reportsTitle: "Generated Reports",
    reportsEmpty: "No reports generated in this audit.",
    docsTitle: "Documents",
    docsEmpty: "No documents uploaded.",
    repCol: "Report", dateCol: "Date / Time",
    view: "View", opening: "Opening…", noFile: "No file",
    uploadBtn: "Upload document", uploading: "Uploading…",
    docNameCol: "Document", uploadedByCol: "Uploaded by", sizeCol: "Size",
    byAdvisor: "Advisor", byAdmin: "GFI",
    uploadErr: "Could not upload the document. Please try again.",
    docRemarks: "Remarks", hideRemarks: "Hide",
    statusCol: "Status", stOk: "Correct", stIssue: "Needs remark", stNone: "Not reviewed",
  },
};

/* Human-readable report-type labels. */
const TYPE_LABELS = {
  es: {
    mass_balance: "Balance de Masa", organic_certs: "Certificados Orgánicos",
    traceability: "Trazabilidad", organic_labels: "Etiquetas Orgánicas",
    general_info: "Información General y Diagrama de Flujo",
    traceability_proc: "Procedimiento de Trazabilidad",
    complaints_proc: "Programa de Recepción y Manejo de Quejas",
    audit_summary: "Reporte Resumen de Auditoría",
  },
  en: {
    mass_balance: "Mass Balance", organic_certs: "Organic Certificates",
    traceability: "Traceability", organic_labels: "Organic Labels",
    general_info: "General Information & Flow Diagram",
    traceability_proc: "Traceability Procedure",
    complaints_proc: "Complaint Reception & Handling Program",
    audit_summary: "Audit Summary Report",
  },
};

function normalizeStatus(s) {
  const v = String(s || "open").toLowerCase();
  return (v === "closed" || v === "completed") ? "closed" : "open";
}

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
  } catch (e) { return String(d); }
}

function fmtSize(bytes) {
  var n = Number(bytes || 0);
  if (!n) return "—";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

function newDocId() {
  return "ADOC-" + Date.now() + "-" + Math.floor(Math.random() * 1e6);
}

export default function AdvisorPortalShell({ lang: langProp, profile }) {
  const [lang, setLang] = useState(langProp === "en" ? "en" : "es");
  const t = T[lang];

  const [audits, setAudits] = useState(null); /* null = loading */
  const [selected, setSelected] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [openDocChat, setOpenDocChat] = useState(null);
  const fileInputRef = useRef(null);

  /* Open a generated report: pull its blob from Storage and show it. */
  const handleViewReport = useCallback(async (entry) => {
    if (!entry || !entry.id || !entry.hasBlob) return;
    setOpeningId(entry.id);
    try {
      const blob = await loadReportBlob(entry.id);
      if (blob) openBlobInTab(blob, (entry.filename || "report") + "");
    } finally {
      setOpeningId(null);
    }
  }, []);

  /* Open an uploaded document. */
  const handleViewDoc = useCallback(async (doc) => {
    if (!doc || !doc.id) return;
    setOpeningId(doc.id);
    try {
      const blob = await loadDocBlob(doc.id);
      if (blob) openBlobInTab(blob, doc.fileName || doc.name || "document");
    } finally {
      setOpeningId(null);
    }
  }, []);

  /* Advisor uploads their own supporting document to the selected audit.
     The doc is tagged uploadedBy:'advisor' so it's distinguishable from
     the admin's uploads. Persists to organic_audits + Storage. */
  /* Advisor toggles a document's review mark: none → ok → issue → none.
     Persists to organic_audits.otherDocs. Marking 'issue' auto-opens the
     document's remarks thread. */
  const handleToggleDocStatus = useCallback(async (doc) => {
    if (!doc || !selected) return;
    const order = { undefined: "ok", "": "ok", none: "ok", ok: "issue", issue: "none" };
    const next = order[doc.reviewStatus || "none"] || "ok";
    try {
      const rows = await loadFromSupabase("organic_audits", { force: true });
      const fresh = (Array.isArray(rows) ? rows : []).find((a) => a.id === selected.id) || selected;
      const otherDocs = (Array.isArray(fresh.otherDocs) ? fresh.otherDocs : []).map((d) =>
        d.id === doc.id ? { ...d, reviewStatus: next, reviewedAt: new Date().toISOString() } : d
      );
      const updated = { ...fresh, otherDocs: otherDocs, updated_at: new Date().toISOString() };
      await saveToSupabase("organic_audits", updated);
      setSelected(updated);
      setAudits((prev) => Array.isArray(prev) ? prev.map((a) => a.id === updated.id ? updated : a) : prev);
      if (next === "issue") setOpenDocChat(doc.id);
    } catch (e) {
      try { window.alert(t.uploadErr); } catch (e2) {}
    }
  }, [selected, t.uploadErr]);

  const handleUpload = useCallback(async (file) => {
    if (!file || !selected) return;
    setUploading(true);
    try {
      const id = newDocId();
      const doc = {
        id: id,
        name: file.name.replace(/\.[^.]+$/, ""),
        category: "advisor",
        uploadedAt: new Date().toISOString(),
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || "application/octet-stream",
        uploadedBy: "advisor",
        uploaderName: (profile && (profile.first_name || "") + " " + (profile.last_name || "")).trim() || "",
      };
      /* Upload the binary first; if it fails we don't register the doc. */
      await saveDocBlob(id, file);
      /* Reload the freshest audit row, append, persist. */
      const rows = await loadFromSupabase("organic_audits", { force: true });
      const fresh = (Array.isArray(rows) ? rows : []).find((a) => a.id === selected.id) || selected;
      const otherDocs = Array.isArray(fresh.otherDocs) ? fresh.otherDocs.slice() : [];
      otherDocs.unshift(doc);
      const updated = { ...fresh, otherDocs: otherDocs, updated_at: new Date().toISOString() };
      await saveToSupabase("organic_audits", updated);
      /* Update local view. */
      setSelected(updated);
      setAudits((prev) => Array.isArray(prev) ? prev.map((a) => a.id === updated.id ? updated : a) : prev);
    } catch (e) {
      try { window.alert(t.uploadErr); } catch (e2) {}
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [selected, profile, t.uploadErr]);

  /* Load all audits (read-only). The advisor sees every audit. */
  useEffect(() => {
    let alive = true;
    loadFromSupabase("organic_audits", { force: true })
      .then((rows) => { if (alive) setAudits(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setAudits([]); });
    return () => { alive = false; };
  }, []);

  const handleLogout = useCallback(async () => {
    try { await supabase.auth.signOut(); } catch (e) {}
  }, []);

  const firstName = (profile && profile.first_name) || "";
  const greeting = firstName ? (t.welcome + ", " + firstName) : t.welcome;

  const sortedAudits = useMemo(() => {
    const arr = Array.isArray(audits) ? audits.slice() : [];
    arr.sort((a, b) => String(b.year || "").localeCompare(String(a.year || "")) || String(b.name || "").localeCompare(String(a.name || "")));
    return arr;
  }, [audits]);

  return (
    <div className="sps-shell">
      <header className="sps-header">
        <div className="sps-header-left" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={LOGO_B64} alt="Ground-Fresh International" style={{ height: 44, width: "auto", display: "block", background: "rgba(255,255,255,0.95)", borderRadius: 6, padding: "3px 6px" }} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 11, letterSpacing: "0.15em", color: "#5BBCB0", fontWeight: 700, textTransform: "uppercase" }}>GFI Nexus</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#ffffff" }}>{t.portal}</span>
          </div>
        </div>

        <div className="sps-header-right" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", marginRight: 4 }}>{greeting}</div>
          <div style={{ display: "inline-flex", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 6, overflow: "hidden" }}>
            {["es", "en"].map((k) => {
              const isOn = lang === k;
              return (
                <button
                  key={k}
                  onClick={() => setLang(k)}
                  style={{
                    background: isOn ? "rgba(91,188,176,0.18)" : "transparent",
                    color: isOn ? "#5BBCB0" : "rgba(255,255,255,0.7)",
                    border: "none", padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.05em",
                  }}
                >{k}</button>
              );
            })}
          </div>
          <button
            onClick={handleLogout}
            style={{ background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >{t.logout}</button>
        </div>
      </header>

      <main style={{ padding: "28px 32px", maxWidth: 1100, margin: "0 auto" }}>
        {!selected && (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--color-text, #1a2636)", margin: "0 0 4px" }}>{t.auditsTitle}</h1>
            <p style={{ fontSize: 13, color: "var(--color-text-muted, #666)", margin: "0 0 20px" }}>{t.auditsSub}</p>

            {audits === null ? (
              <div style={{ padding: 40, textAlign: "center", color: "#888", fontSize: 13 }}>{t.loading}</div>
            ) : sortedAudits.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#888", fontSize: 13, fontStyle: "italic" }}>{t.empty}</div>
            ) : (
              <div style={{ border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                      <th style={{ padding: "10px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>{t.colName}</th>
                      <th style={{ padding: "10px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>{t.colYear}</th>
                      <th style={{ padding: "10px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>{t.colStatus}</th>
                      <th style={{ padding: "10px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>{t.colAuditor}</th>
                      <th style={{ padding: "10px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444", textAlign: "center" }}>{t.colReports}</th>
                      <th style={{ padding: "10px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444", textAlign: "center" }}>{t.colDocs}</th>
                      <th style={{ padding: "10px 14px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAudits.map((a) => {
                      const closed = normalizeStatus(a.status) === "closed";
                      const nReports = Array.isArray(a.reports) ? a.reports.length : 0;
                      const nDocs = Array.isArray(a.otherDocs) ? a.otherDocs.length : 0;
                      return (
                        <tr key={a.id} style={{ borderTop: "1px solid #eef0f2" }}>
                          <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1a2636" }}>{a.name || "—"}</td>
                          <td style={{ padding: "10px 14px", color: "#555" }}>{a.year || "—"}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{
                              fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                              background: closed ? "#fde8e8" : "#e6f4ea",
                              color: closed ? "#b42318" : "#1a7f37",
                            }}>{closed ? t.statusClosed : t.statusOpen}</span>
                          </td>
                          <td style={{ padding: "10px 14px", color: "#555" }}>{a.auditorName || "—"}</td>
                          <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#1a2636" }}>{nReports}</td>
                          <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#1a2636" }}>{nDocs}</td>
                          <td style={{ padding: "10px 14px", textAlign: "right" }}>
                            <button
                              onClick={() => setSelected(a)}
                              style={{ background: "#1a2636", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            >{t.open}</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {selected && (
          <div>
            <button
              onClick={() => setSelected(null)}
              style={{ background: "transparent", border: "none", color: "#1a2636", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 16 }}
            >{t.back}</button>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1a2636", margin: "0 0 4px" }}>{selected.name || "—"}</h1>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>
              {selected.auditorName || "—"} · {fmtDate(selected.startDate)}{selected.endDate ? " — " + fmtDate(selected.endDate) : ""}
            </div>
            {/* Generated reports — read/download only. */}
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2636", textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 10px" }}>{t.reportsTitle}</div>
            {(() => {
              const reports = Array.isArray(selected.reports) ? selected.reports : [];
              const labels = TYPE_LABELS[lang];
              if (reports.length === 0) {
                return <div style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 13, fontStyle: "italic", border: "1px dashed #d1d5db", borderRadius: 10, marginBottom: 24 }}>{t.reportsEmpty}</div>;
              }
              return (
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#fff", marginBottom: 24 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                        <th style={{ padding: "9px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>{t.repCol}</th>
                        <th style={{ padding: "9px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>{t.dateCol}</th>
                        <th style={{ padding: "9px 14px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((r) => {
                        const label = (r && r.title) || labels[r && r.type] || (r && r.type) || "—";
                        let dt = "—";
                        try { dt = r.generatedAt ? new Date(r.generatedAt).toLocaleString(lang === "es" ? "es-MX" : "en-US") : "—"; } catch (e) {}
                        return (
                          <tr key={r.id} style={{ borderTop: "1px solid #eef0f2" }}>
                            <td style={{ padding: "9px 14px", color: "#1a2636" }}>{label}</td>
                            <td style={{ padding: "9px 14px", color: "#555", fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>{dt}</td>
                            <td style={{ padding: "9px 14px", textAlign: "right" }}>
                              {r.hasBlob ? (
                                <button
                                  onClick={() => handleViewReport(r)}
                                  disabled={openingId === r.id}
                                  style={{ background: "#1a2636", color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 600, cursor: openingId === r.id ? "wait" : "pointer", opacity: openingId === r.id ? 0.6 : 1 }}
                                >{openingId === r.id ? t.opening : t.view}</button>
                              ) : (
                                <span style={{ fontSize: 11, color: "#aaa", fontStyle: "italic" }}>{t.noFile}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {/* Documents — advisor can upload their own + view all. */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 10px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2636", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.docsTitle}</div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleUpload(f); }}
                />
                <button
                  onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}
                  disabled={uploading}
                  style={{ background: "#5a7d3c", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: uploading ? "wait" : "pointer", opacity: uploading ? 0.6 : 1 }}
                >{uploading ? t.uploading : "+ " + t.uploadBtn}</button>
              </div>
            </div>
            {(() => {
              const docs = Array.isArray(selected.otherDocs) ? selected.otherDocs : [];
              if (docs.length === 0) {
                return <div style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 13, fontStyle: "italic", border: "1px dashed #d1d5db", borderRadius: 10 }}>{t.docsEmpty}</div>;
              }
              return (
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                        <th style={{ padding: "9px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>{t.docNameCol}</th>
                        <th style={{ padding: "9px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444", textAlign: "center" }}>{t.statusCol}</th>
                        <th style={{ padding: "9px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444" }}>{t.uploadedByCol}</th>
                        <th style={{ padding: "9px 14px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#444", textAlign: "right" }}>{t.sizeCol}</th>
                        <th style={{ padding: "9px 14px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map((d) => {
                        const byAdvisor = d.uploadedBy === "advisor";
                        const chatOpen = openDocChat === d.id;
                        const rows = [
                          <tr key={d.id} style={{ borderTop: "1px solid #eef0f2" }}>
                            <td style={{ padding: "9px 14px", color: "#1a2636" }}>{d.name || d.fileName || "—"}</td>
                            <td style={{ padding: "9px 14px", textAlign: "center" }}>
                              {(() => {
                                const st = d.reviewStatus || "none";
                                const cfg = st === "ok"
                                  ? { bg: "#e6f4ea", color: "#1a7f37", icon: "✓", label: t.stOk }
                                  : st === "issue"
                                  ? { bg: "#fde8e8", color: "#b42318", icon: "✗", label: t.stIssue }
                                  : { bg: "#f3f4f6", color: "#999", icon: "—", label: t.stNone };
                                return (
                                  <button
                                    onClick={() => handleToggleDocStatus(d)}
                                    title={cfg.label}
                                    style={{ background: cfg.bg, color: cfg.color, border: "none", borderRadius: 14, width: 28, height: 28, fontSize: 14, fontWeight: 700, cursor: "pointer", lineHeight: 1 }}
                                  >{cfg.icon}</button>
                                );
                              })()}
                            </td>
                            <td style={{ padding: "9px 14px" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: byAdvisor ? "#eaf2e0" : "#eef0f2", color: byAdvisor ? "#5a7d3c" : "#555" }}>
                                {byAdvisor ? (d.uploaderName || t.byAdvisor) : t.byAdmin}
                              </span>
                            </td>
                            <td style={{ padding: "9px 14px", textAlign: "right", color: "#555", fontFamily: "monospace", fontSize: 12 }}>{fmtSize(d.fileSize)}</td>
                            <td style={{ padding: "9px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                              <button
                                onClick={() => setOpenDocChat(chatOpen ? null : d.id)}
                                style={{ background: chatOpen ? "#eef0f2" : "transparent", color: "#5a7d3c", border: "1px solid #cfe0b8", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", marginRight: 6 }}
                              >💬 {chatOpen ? t.hideRemarks : t.docRemarks}</button>
                              <button
                                onClick={() => handleViewDoc(d)}
                                disabled={openingId === d.id}
                                style={{ background: "#1a2636", color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 600, cursor: openingId === d.id ? "wait" : "pointer", opacity: openingId === d.id ? 0.6 : 1 }}
                              >{openingId === d.id ? t.opening : t.view}</button>
                            </td>
                          </tr>
                        ];
                        if (chatOpen) {
                          rows.push(
                            <tr key={d.id + "-chat"}>
                              <td colSpan={5} style={{ padding: "0 14px 14px", background: "#fafbfc" }}>
                                <AuditComments
                                  auditId={selected.id}
                                  targetType="document"
                                  targetId={d.id}
                                  author={{ name: ((profile && (profile.first_name || "") + " " + (profile.last_name || "")).trim()) || "", role: (profile && profile.role) || "audit_advisor" }}
                                  lang={lang}
                                />
                              </td>
                            </tr>
                          );
                        }
                        return rows;
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {/* Audit-level remarks / chat */}
            <div style={{ marginTop: 24 }}>
              <AuditComments
                auditId={selected.id}
                targetType="audit"
                targetId={null}
                author={{ name: ((profile && (profile.first_name || "") + " " + (profile.last_name || "")).trim()) || "", role: (profile && profile.role) || "audit_advisor" }}
                lang={lang}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
